import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { stageRig } from '../lighting/stage-light-rig.js';

// Shared-rig contract (see the framing addendum). Originally 'forbidden' -
// the ring was to be the only light - but the festival direction wants the
// stage behind it: NeonPulse's rig, beams and floor pools sweeping AROUND the
// ring. zones: 'floor' keeps the beam aims off the camera->artwork axis, so
// the rig dresses the space and never stabs through the cover the ring frames.
const RIG = 'required';
const RIG_OWNER = 'portal';
const RIG_INTENSITY_SCALE = 1;
const RIG_ZONES = 'floor';
const RIG_FLOOR_LIGHT_BOOST = 3;
export { RIG };

// NeonPulse's exact floor: something for the rig's pools of light to land on.
const FLOOR_SIZE = 60;
const FLOOR_COLOR = 0x16161c;
// Dense enough that the floor is gone well before its far edge: at 1/0.11 = 9
// units the floor has faded ~63%, and the edge sits 30 units out.
const FLOOR_FOG_DENSITY = 0.11;

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'portal',
  family: 'vector',
  // 0.80, not the 0.82 it launched with - same reasoning as laser-grid.js's
  // own danceability comment: the pool picks NEAREST profiles, real tracks
  // essentially never index past the low 0.8s, and 0.82 kept this out of
  // the pool entirely. 0.80 puts it in the tier that actually gets drawn.
  danceability: 0.80,
  energyLevel: 0.80,
  bpmMin: 110,
  bpmMax: 175,
  movement: 0.70,
  tonality: 0.70,
  prefers: { gridStrength: [0.60, 1.0], lowEnd: [0.40, 1.0] },
  enabled: true
};

// Portal: one ring of light around the cover, and matter that leaves it on the
// beat. A single gesture, readable from the back of a room. All of the force
// comes from the restraint - one element done properly rather than a stack of
// layers - so almost everything here is about making that one ring exact.
//
// THE CAMERA IS NEVER TOUCHED, and the ring is positioned RELATIVE TO IT.
// scene-manager adds a Ken Burns drift of +-0.22 world units to the camera
// after every update() with no opt-out (see _applyCameraDrift). The ring has
// to hold still against a DOM overlay that does not drift at all: at this
// distance 0.22 units is 5.4% of the frame half-height, about 29px at 1080p,
// i.e. a frame visibly off its picture. Placing the ring a fixed distance in
// front of the camera, oriented like the camera, makes the drift move both
// together and cancel - and as a bonus it makes "a torus seen face on"
// exactly true rather than approximately.
//
// CENTRE AND SIZE ARE BOTH FIXED, matched ONCE to the cover's DEFAULT
// centred layout - deliberately not the live measurement. Two earlier
// versions each got this half wrong: tracking the measured overlay made the
// ring hop between tracks when a title wrapped to a second line, and a
// fixed centre with a measurement-compensated radius made the ring balloon
// the moment lyrics mode moved the overlay to the corner. The rule now is
// the user's own: centred on where the cover sits in its default layout,
// and NOTHING about the ring reacts when the cover moves or the overlay's
// measured rectangle changes. (The safe-zone dimming in the shaders still
// tracks the live overlay - that is its job; the ring's geometry does not.)

const RING_SEGMENTS = 128;
// The fixed centre, in the safe zone's own "height units" (1.0 = half the
// frame height, y up). x = 0: dead centre horizontally. y matches
// safeZone.glsl.js's own SAFE_CENTER_Y - the cover's canonical centre in
// the default layout, measured once there (46.2px above centre at 1080p).
// Keep the two in sync by hand if that one ever moves.
const RING_CENTER_X = 0;
const RING_CENTER_Y = 0.0855;
// The overlay's half-HEIGHT in the default centred layout, matching
// safeZone.glsl.js's own SAFE_HALF_HEIGHT fallback - the larger of the
// block's two half-dimensions, so it is what the ring must clear. Fixed for
// the same reason the centre is: the ring is sized to the cover's home
// position once, not to wherever the overlay happens to be measured now.
const OVERLAY_REACH = 0.60;
const RING_DIST = 6.0;          // ring plane, in front of the camera
// 8%, not the 35% it launched with: "il doit etre juste autour de la zone
// pochette-titre". At 35% the main ring (and the outer one riding at its
// own multiple) filled the frame and buried the beams/fragments the piece
// exists to show - the ring is a snug frame around the block now, and the
// space outside it is where the effects live.
const SAFE_MARGIN = 1.08;
// Tube cross-section, in pixels AT A 720p-EQUIVALENT FRAME, converted once
// per frame into world units at the ring's depth. NOT via uPixelScale and the
// per-vertex depth any more: that needed the real drawing-buffer height,
// which scene-manager derives as innerHeight x BASE_RENDER_SCALE(0.94) x
// min(dpr,1) x TIER_RESOLUTION_SCALE - measured 575px where the code assumed
// 1440 on a Retina screen, i.e. a ring 2.5x too thin. (LaserCathedral's
// beams carry the same bias and survive only because dozens of them overlap
// additively.) World units sized against the frame height at the ring's own
// depth cannot be wrong by construction, whatever the buffer scale is doing.
const TUBE_PX = 20;
const REF_HEIGHT_PX = 720;
// Pulled in with SAFE_MARGIN's own tightening (1.35 -> 1.22): at 1.35x a
// snug inner ring still put the outer one at ~88% of the half-height, most
// of the way back to the frame-filling look the tightening removes.
const SECOND_RING_RADIUS = 1.22;
const SECOND_RING_THICK = 0.5;
const SECOND_RING_OPACITY = 0.5;
const SECOND_RING_SPIN = -0.13;  // counter-rotating, rad/s
// 0.30: measured through the real chain both ways. At 0.25 the core was a
// 4px line lost in fog; at 0.42 the white core plus its own bloom swallowed
// the colour gradient whole and the ring rendered white.
const CORE_FRAC = 0.30;
const PEAK_LUMINANCE = 0.94;

// Circumference modulation. Gaussians wide enough to overlap, so the ring
// breathes unevenly instead of pulsing as one.
const BAND_SIGMA = 0.95;
const THICK_SWING = 0.9;        // the ring breathes HARD - club, not gallery

// Fragments.
const FRAG_LEN_MIN = 0.15;
const FRAG_LEN_MAX = 0.5;
const FRAG_ARC_SEGMENTS = 14;
const FRAG_TRAVEL_BEATS = 2;    // criterion 3
// Exactly the distance to the camera, so "two beats" is literally true and
// measurable. It is fully faded out well before it arrives (see
// FRAG_FADE_FROM), and the shader floors the view depth, so reaching the lens
// plane costs nothing.
const FRAG_TRAVEL_FRAC = 1.0;
// CONVERGENCE, not growth - and this replaces the brief's "son rayon
// augmente legerement" for a measured reason. A fragment is born on the ring
// at 25 degrees off the optical axis in a 34-degree vertical half-field;
// advancing in Z while its radius GROWS pushed it past the frame edge at 22%
// of its two-beat life - 0.2s of visibility, which is why no fragment was
// ever identifiable on screen. Pulling it 72% of the way toward the axis as
// it comes keeps it in frame for ~70% of the trip; its fixed WORLD thickness
// meanwhile balloons on screen as the depth collapses, so it still grows and
// still exits by the edges - during its own fade-out, as the brief wanted.
const FRAG_CONVERGE = 0.72;
const FRAG_FADE_FROM = 0.70;    // fades over the last 30%
const FRAG_THICK_MULT = 1.35;
const NOTCH_CLOSE_SEC = 0.4;
const NOTCH_WIDTH = 0.22;
const MAX_NOTCHES = 4;
const NO_BPM_MIN_GAP = 0.4;
const NO_BPM_RISE = 0.16;

const BAR_LEN_MULT = 1.5;
const PHRASE_CONTRACT = 0.08;
const PHRASE_CONTRACT_SEC = 0.35;

// How much of the frame half-height the OUTERMOST ring may occupy. With the
// snug SAFE_MARGIN this clamp no longer binds in the default layout - it
// stays as the guard it always was, not the thing that sets the size.
const FRAME_FIT = 0.94;
// Halved with SAFE_MARGIN's tightening: a +-10% swing on a ring only 8%
// clear of the block spent half its cycle pinned on the MIN_MARGIN floor.
const PROGRESS_RADIUS_SWING = 0.05;
// Hard floor: the ring never comes closer to the overlay than this, whatever
// the clamp and the progress swing would prefer.
const MIN_MARGIN = 1.03;
const RAMP_PERIOD = 40;         // one revolution of the gradient
const PALETTE_STOPS = 5;
const MIN_SATURATION = 0.85;
const GLOW_STRENGTH = 0.14;
const GLOW_KICK = 2.2;       // the haze answers the kick
const PULSE_LIFT = 0.22;

const TIER = {
  0: { rings: 1, fragments: 12, glow: false },
  1: { rings: 2, fragments: 16, glow: true },
  2: { rings: 2, fragments: 24, glow: true }
};
const DEFAULT_TIER = 1;

const TWO_PI = Math.PI * 2;
// Angle convention: 0 is right, +PI/2 is the TOP of the screen, -PI/2 the
// bottom. low lives at the bottom, high at the top, mid on both sides.
const ANGLE_LOW = -Math.PI / 2;
const ANGLE_HIGH = Math.PI / 2;
const ANGLE_MID_A = 0;
const ANGLE_MID_B = Math.PI;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function wrapPi(a) {
  let d = a % TWO_PI;
  if (d > Math.PI) d -= TWO_PI;
  if (d < -Math.PI) d += TWO_PI;
  return d;
}
function urlFlag(name) {
  if (typeof location === 'undefined') return false;
  const v = new URLSearchParams(location.search).get(name);
  return v !== null && v !== '0' && v !== 'false';
}
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const CLAMP_CHUNK = `
  vec3 clampLuminance(vec3 col) {
    float mx = max(col.r, max(col.g, col.b));
    return mx > ${PEAK_LUMINANCE} ? col * (${PEAK_LUMINANCE} / mx) : col;
  }
`;

// The palette gradient, evaluated without a texture and without dynamic
// indexing: five fixed stops, each contributing a triangular weight, so the
// two nearest stops interpolate and the weights sum to one.
const RAMP_CHUNK = `
  uniform vec3 uPal[${PALETTE_STOPS}];
  vec3 rampAt(float u) {
    float t = fract(u) * ${PALETTE_STOPS}.0;
    vec3 c = vec3(0.0);
    for (int i = 0; i < ${PALETTE_STOPS}; i++) {
      float d = abs(t - float(i));
      d = min(d, ${PALETTE_STOPS}.0 - d);
      c += uPal[i] * max(0.0, 1.0 - d);
    }
    return c;
  }
`;

const SAFE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  // Local copy with a 0.5 floor rather than the shared 0.32: fragments pass in
  // FRONT of the cover on their way out, and at 0.32 they vanish exactly as
  // they cross it, which reads as them being swallowed rather than flying past.
  float safeZoneAttenuation(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.5, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const ringVertexShader = `
  precision mediump float;

  attribute float aAngle;
  attribute float aSide;
  attribute float aThick;
  attribute float aU;

  uniform float uRadius;
  uniform float uWorldPerPx;
  uniform float uRampRot;
  uniform float uOpacity;
  uniform float uThickMul;
  uniform float uPulse;
  uniform vec4 uNotchAngle;
  uniform vec4 uNotchAmt;

  varying float vSide;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafe;

  ${RAMP_CHUNK}

  void main() {
    vec2 dir = vec2(cos(aAngle), sin(aAngle));
    vec3 centre = vec3(dir * uRadius, 0.0);
    float halfW = aThick * uThickMul * 0.5 * uWorldPerPx;

    vec3 pos = centre + vec3(dir * (aSide * halfW), 0.0);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);

    vSide = aSide;
    vColor = rampAt(aU + uRampRot);

    // The notch. Without it a fragment appears to come from nowhere; with it,
    // the ring visibly gives up the material and then heals.
    float att = 1.0;
    for (int i = 0; i < ${MAX_NOTCHES}; i++) {
      float d = aAngle - uNotchAngle[i];
      d = mod(d + ${Math.PI.toFixed(6)}, ${TWO_PI.toFixed(6)}) - ${Math.PI.toFixed(6)};
      att *= 1.0 - uNotchAmt[i] * exp(-(d * d) / ${(2 * NOTCH_WIDTH * NOTCH_WIDTH).toFixed(6)});
    }
    vGain = uOpacity * att * uPulse;
    // NO safe-zone attenuation on the ring - and this is the bug that killed
    // the whole picture, so it stays written down. The overlay rectangle is
    // 0.48 x 0.61 with a 0.2 feather: nearly the entire circumference sits
    // inside that feather, so the ring was dimmed to ~0.67 - putting its 0.94
    // core at 0.63, BELOW the 0.78 bloom threshold - everywhere except the
    // two horizontal crossings, the only points outside the feather. Those
    // two points were the "deux lueurs diffuses" in the capture: the only
    // places the ring was allowed to bloom. The ring encircles the cover BY
    // CONSTRUCTION; attenuating it against the cover protects nothing.
    vSafe = 1.0;
  }
`;

const ringFragmentShader = `
  precision mediump float;

  varying float vSide;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafe;

  ${CLAMP_CHUNK}

  void main() {
    float u = abs(vSide);
    // LaserCathedral's two-zone profile, unchanged: a near-white core over the
    // central quarter of the tube inside a coloured halo. The core is what
    // crosses the 0.78 bloom threshold and makes this read as light rather
    // than as a tinted line.
    float core = 1.0 - smoothstep(${(CORE_FRAC*0.4).toFixed(3)}, ${CORE_FRAC.toFixed(3)}, u);
    // Flatter than LaserCathedral's (1-u)^2, and stronger than its 0.55: a
    // cathedral has dozens of thin beams adding up, this piece has ONE ring,
    // and through the real chain (ACES + vignette) the squared falloff left
    // the outer half of the tube under the grain floor - the ring read as a
    // bare core line in a fog. A ribbon needs its halo to HOLD width.
    float halo = pow(1.0 - u, 1.3);
    // 0.62, not 0.85 white: the bloom whitens the core again on its own, and
    // at 0.85 the whole ring read as white neon with the palette gone.
    vec3 hot = mix(vColor, vec3(1.0), 0.62);
    vec3 col = mix(vColor * halo * 0.95, hot, core);
    col *= vGain * vSafe;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

const fragVertexShader = `
  precision mediump float;

  attribute float aT;
  attribute float aSide;

  attribute float iStart;
  attribute float iLen;
  attribute float iBirth;
  attribute float iLife;
  attribute float iThick;
  attribute vec3 iColor;

  uniform float uTime;
  uniform float uRadius;
  uniform float uWorldPerPx;
  uniform float uPulse;
  uniform float uTravelZ;

  varying float vSide;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafe;

  ${SAFE_CHUNK}

  void main() {
    float age = uTime - iBirth;
    float prog = clamp(age / max(iLife, 0.0001), 0.0, 1.0);
    float alive = step(0.0, age) * (1.0 - step(iLife, age));

    float ang = iStart + aT * iLen;
    float R = uRadius * (1.0 - ${FRAG_CONVERGE.toFixed(3)} * prog);
    // +Z in this group points back at the camera, so this is the travel.
    vec3 centre = vec3(cos(ang) * R, sin(ang) * R, prog * uTravelZ);

    // World thickness, deliberately: a slab of fixed world size gets FATTER
    // on screen as it flies at the lens, which is exactly the aggression a
    // club drop wants and exactly what a screen-locked width was suppressing.
    vec2 dir = vec2(cos(ang), sin(ang));
    vec3 pos = centre + vec3(dir * (aSide * iThick * 0.5 * uWorldPerPx), 0.0);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    vSide = aSide;
    // It keeps the colour it had at the angle it left from, all the way out.
    vColor = iColor;
    vGain = alive * uPulse * (1.0 - smoothstep(${FRAG_FADE_FROM.toFixed(2)}, 1.0, prog));
    vSafe = safeZoneAttenuation(gl_Position);
  }
`;

const glowVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const glowFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uAspect;
  uniform float uGain;
  uniform vec2 uCenter;
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0) * 2.0 - uCenter;
    float g = 1.0 - clamp(length(p) * 0.62, 0.0, 1.0);
    vec3 col = uTint * (g * g) * uGain;
    // Wrapped to a 64px tile: mediump is real fp16 on the television, where
    // fract() of a raw gl_FragCoord product keeps no fractional part at all.
    vec2 fcw = mod(floor(gl_FragCoord.xy), 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col + dith, vec3(0.0)), 1.0);
  }
`;

export class PortalVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._debugFrag = urlFlag('portalfrag');
    this._debugRing = urlFlag('portalring');
    // ?portallayer=ring|fragments - one element at a time; ?portalthick=<px>
    // forces the tube thickness. Both exist to make failures measurable
    // instead of describable.
    this._layerOnly = urlParam('portallayer');
    const ft = parseFloat(urlParam('portalthick'));
    this._forcedThick = Number.isFinite(ft) && ft > 0 ? ft : null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._layout = layout;

    this._group = new THREE.Group();
    this._group.matrixAutoUpdate = false;
    scene.add(this._group);

    // The stage behind the ring.
    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES,
      floorLightBoost: RIG_FLOOR_LIGHT_BOOST
    });
    const floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: FLOOR_COLOR,
      roughness: 0.78,
      metalness: 0.0
    });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    scene.add(this._floor);
    // Black exponential fog (LaserCathedral's device): the floor fades out
    // before its far edge, so no horizon line cuts across the frame behind
    // the ring. Only the MeshStandardMaterial floor respects scene.fog - the
    // rings, fragments and glow are ShaderMaterials without a fog chunk, so
    // they stay untouched by it.
    scene.fog = new THREE.FogExp2(0x000000, FLOOR_FOG_DENSITY);

    this._pal = [];
    for (let i = 0; i < PALETTE_STOPS; i++) this._pal.push(new THREE.Color(0x39a0ff));

    this._ringUniformsShared = {
      uWorldPerPx: { value: 0.008 },
      uRampRot: { value: 0 },
      uPulse: { value: 1 },
      uPal: { value: this._pal }
    };

    // Club chain: less vignette (it was crushing a ring that lives at 0.7 of
    // the frame half-height), more bloom.
    this.postProcessing = {
      vignette: 0.22,
      bloomStrength: 0.85
    };

    // Created before the rings: _buildRing hands these holder objects to the
    // materials, so they have to be the same objects update() later mutates.
    this._notchAngle = new THREE.Vector4(0, 0, 0, 0);
    this._notchAmt = new THREE.Vector4(0, 0, 0, 0);
    this._zeroNotch = new THREE.Vector4(0, 0, 0, 0);

    this._rings = [];
    this._ringA = this._buildRing(1, 1, 1, true);
    this._rings.push(this._ringA);
    if (layout.rings > 1) {
      // No notches on the outer ring: it emits nothing, and it counter-rotates,
      // so a notch drawn in its own angle space would drift away from the gap
      // it is supposed to explain.
      this._ringB = this._buildRing(SECOND_RING_RADIUS, SECOND_RING_THICK, SECOND_RING_OPACITY, false);
      this._rings.push(this._ringB);
    } else {
      this._ringB = null;
    }

    this._buildFragments(layout.fragments);

    if (layout.glow) {
      this._glowUniforms = {
        uTint: { value: new THREE.Color(0x2050a0) },
        uAspect: safeZoneUniforms.uAspect,
        uGain: { value: GLOW_STRENGTH },
        // Own fixed value, no longer the shared measured-overlay holder -
        // the haze must stay concentric with the ring (see RING_CENTER_X/Y).
        uCenter: { value: new THREE.Vector2(RING_CENTER_X, RING_CENTER_Y) }
      };
      this._glowGeo = new THREE.PlaneGeometry(1, 1);
      // ADDITIVE and drawn LAST, not an opaque backdrop drawn first: as a
      // backdrop it tinted only the void above the floor, and the floor's
      // silhouette cut it off in a clean horizontal line across the frame.
      // Added on top of everything it lights the floor and the void equally,
      // so there is no boundary for the eye to find - on black, addition is
      // identical to what the backdrop was.
      this._glowMat = new THREE.ShaderMaterial({
        uniforms: this._glowUniforms,
        vertexShader: glowVertexShader,
        fragmentShader: glowFragmentShader,
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthTest: false,
        depthWrite: false
      });
      this._glowMesh = new THREE.Mesh(this._glowGeo, this._glowMat);
      this._glowMesh.frustumCulled = false;
      this._glowMesh.renderOrder = 1000;
      scene.add(this._glowMesh);
    }

    // State.
    this._time = 0;
    this._radius = 1;
    this._fragLife = 1.0;
    this._contract = 1;
    this._contractUntil = -1;
    this._lastBeatIndex = null;
    this._lastBarIndex = null;
    this._lastPhraseIndex = null;
    this._lastPaletteRef = undefined;
    this._nextFree = 0;
    this._notchBirth = [-1, -1, -1, -1];
    this._nextNotch = 0;
    this._bands = { low: 0.5, mid: 0.5, high: 0.5 };
    this._lowPrev = 0.5;
    this._lastNoBpmSpawn = -1;

    // Scratch - update() allocates nothing.
    this._sPos = new THREE.Vector3();
    this._sRight = new THREE.Vector3();
    this._sUp = new THREE.Vector3();
    this._sFwd = new THREE.Vector3();

    // Layer isolation.
    if (this._layerOnly === 'ring') {
      this._fragMesh.visible = false;
      if (this._glowMesh) this._glowMesh.visible = false;
    } else if (this._layerOnly === 'fragments') {
      for (let i = 0; i < this._rings.length; i++) this._rings[i].mesh.visible = false;
      if (this._glowMesh) this._glowMesh.visible = false;
    }

    this._applyPalette(null);
    this._placeGroup();

    if (this._debugFrag && typeof window !== 'undefined') {
      const self = this;
      window.portalFragment = function () { self._spawn('beat'); };
    }
    if (this._debugRing) this._makeRingDebug();
  }

  // One ring: a closed strip of RING_SEGMENTS quads. Thickness is a per-vertex
  // attribute rewritten each frame - 258 floats, which is the whole cost of
  // the circumference modulation.
  _buildRing(radiusMul, thickMul, opacity, notched) {
    const N = RING_SEGMENTS;
    const verts = (N + 1) * 2;
    const pos = new Float32Array(verts * 3);      // unused, but three wants one
    const aAngle = new Float32Array(verts);
    const aSide = new Float32Array(verts);
    const aThick = new Float32Array(verts);
    const aU = new Float32Array(verts);
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * TWO_PI;
      for (let s = 0; s < 2; s++) {
        const v = i * 2 + s;
        aAngle[v] = a;
        aSide[v] = s === 0 ? -1 : 1;
        aThick[v] = TUBE_PX;
        aU[v] = i / N;
      }
    }
    const idx = new Uint16Array(N * 6);
    for (let i = 0; i < N; i++) {
      const a = i * 2, b = i * 2 + 1, c = i * 2 + 2, d = i * 2 + 3;
      idx.set([a, b, c, b, d, c], i * 6);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aAngle', new THREE.BufferAttribute(aAngle, 1));
    geo.setAttribute('aSide', new THREE.BufferAttribute(aSide, 1));
    geo.setAttribute('aThick', new THREE.BufferAttribute(aThick, 1));
    geo.setAttribute('aU', new THREE.BufferAttribute(aU, 1));
    geo.setIndex(new THREE.BufferAttribute(idx, 1));

    const uniforms = Object.assign({}, safeZoneUniforms, this._ringUniformsShared, {
      uRadius: { value: 1 },
      uOpacity: { value: opacity },
      uThickMul: { value: thickMul },
      uNotchAngle: { value: this._notchAngle },
      uNotchAmt: { value: notched ? this._notchAmt : this._zeroNotch }
    });
    const mat = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: ringVertexShader,
      fragmentShader: ringFragmentShader,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    this._group.add(mesh);
    return { mesh: mesh, geo: geo, mat: mat, uniforms: uniforms, aThick: aThick, radiusMul: radiusMul, spin: 0 };
  }

  _buildFragments(count) {
    const M = FRAG_ARC_SEGMENTS;
    const verts = (M + 1) * 2;
    const pos = new Float32Array(verts * 3);
    const aT = new Float32Array(verts);
    const aSide = new Float32Array(verts);
    for (let i = 0; i <= M; i++) {
      for (let s = 0; s < 2; s++) {
        const v = i * 2 + s;
        aT[v] = i / M;
        aSide[v] = s === 0 ? -1 : 1;
      }
    }
    const idx = new Uint16Array(M * 6);
    for (let i = 0; i < M; i++) {
      const a = i * 2, b = i * 2 + 1, c = i * 2 + 2, d = i * 2 + 3;
      idx.set([a, b, c, b, d, c], i * 6);
    }
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aT', new THREE.BufferAttribute(aT, 1));
    geo.setAttribute('aSide', new THREE.BufferAttribute(aSide, 1));
    geo.setIndex(new THREE.BufferAttribute(idx, 1));

    // The ring buffer, pre-allocated. Nothing is created at a beat; a slot is
    // overwritten and its birth time reset.
    this._fragCount = count;
    this._iStart = new Float32Array(count);
    this._iLen = new Float32Array(count);
    this._iBirth = new Float32Array(count).fill(-1e9);
    this._iLife = new Float32Array(count).fill(1);
    this._iThick = new Float32Array(count).fill(TUBE_PX);
    this._iColor = new Float32Array(count * 3);
    geo.setAttribute('iStart', new THREE.InstancedBufferAttribute(this._iStart, 1));
    geo.setAttribute('iLen', new THREE.InstancedBufferAttribute(this._iLen, 1));
    geo.setAttribute('iBirth', new THREE.InstancedBufferAttribute(this._iBirth, 1));
    geo.setAttribute('iLife', new THREE.InstancedBufferAttribute(this._iLife, 1));
    geo.setAttribute('iThick', new THREE.InstancedBufferAttribute(this._iThick, 1));
    geo.setAttribute('iColor', new THREE.InstancedBufferAttribute(this._iColor, 3));
    geo.instanceCount = count;

    this._fragUniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uRadius: { value: 1 },
      uWorldPerPx: this._ringUniformsShared.uWorldPerPx,
      uPulse: this._ringUniformsShared.uPulse,
      uTravelZ: { value: RING_DIST * FRAG_TRAVEL_FRAC }
    });
    this._fragMat = new THREE.ShaderMaterial({
      uniforms: this._fragUniforms,
      vertexShader: fragVertexShader,
      fragmentShader: ringFragmentShader,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    this._fragGeo = geo;
    this._fragMesh = new THREE.Mesh(geo, this._fragMat);
    this._fragMesh.frustumCulled = false;
    this._group.add(this._fragMesh);
  }

  // Colours: the cover's own, forced to a pop saturation, as five stops that
  // the shader interpolates around the circumference.
  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : [0x39a0ff, 0xff3ba7, 0xffd23b, 0x3bffc4, 0x9b5bff];
    const hsl = { h: 0, s: 0, l: 0 };
    for (let i = 0; i < PALETTE_STOPS; i++) {
      const c = vivid[i % vivid.length];
      this._pal[i].set(c).getHSL(hsl);
      this._pal[i].setHSL(hsl.h, Math.max(MIN_SATURATION, hsl.s), Math.max(0.5, Math.min(0.68, hsl.l)));
    }
    if (this._glowUniforms) {
      const dom = (palette && palette.dominantColors && palette.dominantColors.length)
        ? palette.dominantColors[0] : vivid[0];
      this._glowUniforms.uTint.value.set(dom);
    }
  }

  // Energy around the circumference. Gaussians on the three band centres, wide
  // enough to overlap, so where the ring swells says which band is loud.
  _energyAt(angle) {
    const b = this._bands;
    const s2 = 2 * BAND_SIGMA * BAND_SIGMA;
    let dl = wrapPi(angle - ANGLE_LOW);
    let dh = wrapPi(angle - ANGLE_HIGH);
    let dma = wrapPi(angle - ANGLE_MID_A);
    let dmb = wrapPi(angle - ANGLE_MID_B);
    const wl = Math.exp(-(dl * dl) / s2);
    const wh = Math.exp(-(dh * dh) / s2);
    const wm = Math.exp(-(dma * dma) / s2) + Math.exp(-(dmb * dmb) / s2);
    const sum = wl + wh + wm;
    if (sum < 1e-6) return 0;
    return (b.low * wl + b.high * wh + b.mid * wm) / sum;
  }

  _updateThickness() {
    for (let r = 0; r < this._rings.length; r++) {
      const ring = this._rings[r];
      const arr = ring.aThick;
      const N = RING_SEGMENTS;
      for (let i = 0; i <= N; i++) {
        const a = (i / N) * TWO_PI;
        const t = this._forcedThick != null
          ? this._forcedThick
          : TUBE_PX * (1 + THICK_SWING * this._energyAt(a));
        arr[i * 2] = t;
        arr[i * 2 + 1] = t;
      }
      ring.geo.attributes.aThick.needsUpdate = true;
    }
  }

  // Which angle a fragment leaves from: the loudest band decides, so a kick
  // throws material off the bottom and a hat off the top.
  _angleForBands() {
    const b = this._bands;
    if (b.low >= b.mid && b.low >= b.high) return ANGLE_LOW;
    if (b.high >= b.mid && b.high >= b.low) return ANGLE_HIGH;
    return Math.random() < 0.5 ? ANGLE_MID_A : ANGLE_MID_B;
  }

  _colourAt(angle, out) {
    // Same gradient the ring shader evaluates, so a fragment leaves with
    // exactly the colour of the arc it came from.
    let u = (angle / TWO_PI) + this._rampRot();
    u = u - Math.floor(u);
    const t = u * PALETTE_STOPS;
    let acc0 = 0, acc1 = 0, acc2 = 0;
    for (let i = 0; i < PALETTE_STOPS; i++) {
      let d = Math.abs(t - i);
      d = Math.min(d, PALETTE_STOPS - d);
      const w = Math.max(0, 1 - d);
      acc0 += this._pal[i].r * w;
      acc1 += this._pal[i].g * w;
      acc2 += this._pal[i].b * w;
    }
    out[0] = acc0; out[1] = acc1; out[2] = acc2;
  }

  _rampRot() { return (this._time / RAMP_PERIOD) % 1; }

  _emit(angle, angLen, life) {
    const i = this._nextFree;
    this._nextFree = (this._nextFree + 1) % this._fragCount;
    this._iStart[i] = angle - angLen * 0.5;
    this._iLen[i] = angLen;
    this._iBirth[i] = this._time;
    this._iLife[i] = life;
    // Fatter than the ring they left: they are the gesture.
    this._iThick[i] = TUBE_PX * FRAG_THICK_MULT * (1 + THICK_SWING * this._energyAt(angle));
    const c = this._scratchColor || (this._scratchColor = [0, 0, 0]);
    this._colourAt(angle, c);
    this._iColor[i * 3] = c[0];
    this._iColor[i * 3 + 1] = c[1];
    this._iColor[i * 3 + 2] = c[2];
    const at = this._fragGeo.attributes;
    at.iStart.needsUpdate = true;
    at.iLen.needsUpdate = true;
    at.iBirth.needsUpdate = true;
    at.iLife.needsUpdate = true;
    at.iThick.needsUpdate = true;
    at.iColor.needsUpdate = true;

    // The notch the fragment leaves behind.
    const n = this._nextNotch;
    this._nextNotch = (this._nextNotch + 1) % MAX_NOTCHES;
    this._notchBirth[n] = this._time;
    if (n === 0) this._notchAngle.x = angle;
    else if (n === 1) this._notchAngle.y = angle;
    else if (n === 2) this._notchAngle.z = angle;
    else this._notchAngle.w = angle;
  }

  // The hierarchy. Beat / bar / phrase, never a strobe: what changes is how
  // MUCH material leaves and from how many places, not the brightness.
  _spawn(kind) {
    const life = this._fragLife;
    const base = this._angleForBands();
    const jitter = (Math.random() * 2 - 1) * 0.35;
    if (kind === 'phrase') {
      const len = lerp(FRAG_LEN_MIN, FRAG_LEN_MAX, 0.9);
      for (let i = 0; i < 6; i++) this._emit(base + jitter + i * (TWO_PI / 6), len, life);
      this._contractUntil = this._time + PHRASE_CONTRACT_SEC;
    } else if (kind === 'bar') {
      const len = lerp(FRAG_LEN_MIN, FRAG_LEN_MAX, 0.75) * BAR_LEN_MULT;
      this._emit(base + jitter, Math.min(len, FRAG_LEN_MAX), life);
      this._emit(base + jitter + Math.PI, Math.min(len, FRAG_LEN_MAX), life);
    } else {
      const len = lerp(FRAG_LEN_MIN, FRAG_LEN_MAX, 0.35 + Math.random() * 0.3);
      this._emit(base + jitter, len, life);
    }
  }

  // The ring lives a fixed distance in front of the camera, oriented like it,
  // at the FIXED centre (see RING_CENTER_X/Y) - deliberately NOT the
  // measured overlay midpoint, which shifts with the title's own line count
  // while the cover itself never moves.
  _placeGroup() {
    const cam = this._camera;
    if (!cam) return;
    cam.updateMatrixWorld();
    const e = cam.matrixWorld.elements;
    this._sRight.set(e[0], e[1], e[2]);
    this._sUp.set(e[4], e[5], e[6]);
    this._sFwd.set(e[8], e[9], e[10]);   // +Z of the camera, i.e. backwards

    const fov = (cam.fov || 68) * Math.PI / 180;
    const halfH = RING_DIST * Math.tan(fov / 2);

    this._sPos.copy(cam.position)
      .addScaledVector(this._sFwd, -RING_DIST)
      .addScaledVector(this._sRight, RING_CENTER_X * halfH)
      .addScaledVector(this._sUp, RING_CENTER_Y * halfH);

    this._group.position.copy(this._sPos);
    this._group.quaternion.copy(cam.quaternion);
    this._group.updateMatrix();
    this._group.updateMatrixWorld(true);
  }

  _makeRingDebug() {
    const cv = document.createElement('canvas');
    cv.width = 512; cv.height = 130;
    cv.style.cssText = 'position:fixed;left:10px;bottom:10px;z-index:99999;'
      + 'background:rgba(0,0,0,.7);border-radius:6px';
    document.body.appendChild(cv);
    this._ringDebugCanvas = cv;
    this._ringDebugCtx = cv.getContext('2d');
    this._ringDebugAccum = 0;
  }

  _drawRingDebug() {
    const x = this._ringDebugCtx;
    const W = 512, H = 130;
    x.clearRect(0, 0, W, H);
    x.fillStyle = 'rgba(0,0,0,.7)'; x.fillRect(0, 0, W, H);
    const arr = this._rings[0].aThick;
    x.strokeStyle = '#6fe'; x.lineWidth = 2; x.beginPath();
    for (let i = 0; i <= RING_SEGMENTS; i++) {
      const t = arr[i * 2];
      const px = (i / RING_SEGMENTS) * W;
      const py = H - 8 - (t / (TUBE_PX * (1 + THICK_SWING))) * (H - 26);
      if (i === 0) x.moveTo(px, py); else x.lineTo(px, py);
    }
    x.stroke();
    x.fillStyle = '#9ab'; x.font = '11px ui-monospace,monospace';
    x.fillText('thickness px vs angle   low ' + this._bands.low.toFixed(2)
      + '  mid ' + this._bands.mid.toFixed(2) + '  high ' + this._bands.high.toFixed(2), 8, 14);
    // Mark the band centres.
    const mark = (ang, label) => {
      let u = ang / TWO_PI; u = u - Math.floor(u);
      const px = u * W;
      x.strokeStyle = '#f80'; x.beginPath(); x.moveTo(px, H - 8); x.lineTo(px, H - 20); x.stroke();
      x.fillStyle = '#f80'; x.fillText(label, px + 3, H - 10);
    };
    mark(ANGLE_LOW, 'low'); mark(ANGLE_MID_A, 'mid'); mark(ANGLE_HIGH, 'high'); mark(ANGLE_MID_B, 'mid');
  }

  update(deltaTime, profile) {
    // The rig registry runs before the pause gate, exactly as NeonPulse does:
    // it handles pause itself and owns fade-in/out across swaps.
    stageRig.update(deltaTime, profile, RIG_OWNER);

    // Pause is a full freeze - fragments hold their position in flight.
    if (profile.playbackState !== 'PLAYING') return;
    this._time += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    if (bands) {
      this._bands.low = clamp01(bands.low);
      this._bands.mid = clamp01(bands.mid);
      this._bands.high = clamp01(bands.high);
    }

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }

    // --- geometry: radius sized to the DEFAULT layout, once (see the
    // OVERLAY_REACH comment) - only the progress swing varies per frame ---
    const cam = this._camera;
    const fov = (cam && cam.fov ? cam.fov : 68) * Math.PI / 180;
    const halfH = RING_DIST * Math.tan(fov / 2);
    // The margin is taken on the larger half-DIMENSION of the overlay
    // block (its 0.60 half-height), not on the circle through its corners
    // (0.776): a ring clearing the corners plus any margin at all would
    // fill most of a 16:9 frame, which is exactly the look the snug
    // SAFE_MARGIN exists to avoid. The main ring is a closed circle at
    // every moment of the track; the rectangle's bare corners fall outside
    // it, which costs nothing because the text there is centred and
    // narrower than its own bounding box.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const progressMul = 1 + PROGRESS_RADIUS_SWING * Math.sin(Math.PI * 2 * progress - Math.PI / 2);
    const wanted = OVERLAY_REACH * SAFE_MARGIN * progressMul;
    // The outer ring decides how big the inner one may grow...
    const maxInner = FRAME_FIT / (this._ringB ? SECOND_RING_RADIUS : 1);
    // ...but NEVER below the overlay's own reach, and the floor outranks
    // the clamp: if the two constraints collide, the faint outer ring
    // clipping the frame edge is the cheap casualty; the main ring crossing
    // the cover is the expensive one.
    const floor = OVERLAY_REACH * MIN_MARGIN;
    this._radiusUnits = Math.max(Math.min(wanted, maxInner), floor);
    this._radius = this._radiusUnits * halfH;

    // Phrase contraction, easing back.
    if (this._time < this._contractUntil) {
      const k = (this._contractUntil - this._time) / PHRASE_CONTRACT_SEC;
      this._contract = 1 - PHRASE_CONTRACT * Math.sin(Math.PI * clamp01(k));
    } else {
      this._contract = 1;
    }

    // --- beats ---
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || null;
    this._fragLife = bpm ? (60 / bpm) * FRAG_TRAVEL_BEATS : 1.0;

    const bi = profile.beatIndex;
    if (bi != null && bpm) {
      if (this._lastBeatIndex != null && bi !== this._lastBeatIndex) {
        const pi = profile.phraseIndex, bri = profile.barIndex;
        if (pi != null && this._lastPhraseIndex != null && pi !== this._lastPhraseIndex) this._spawn('phrase');
        else if (bri != null && this._lastBarIndex != null && bri !== this._lastBarIndex) this._spawn('bar');
        else this._spawn('beat');
      }
      this._lastBeatIndex = bi;
      this._lastPhraseIndex = profile.phraseIndex;
      this._lastBarIndex = profile.barIndex;
    } else {
      // No tempo: fire on rises in the low band, rate-limited. The modulation
      // and everything else is unchanged, so the piece still reads.
      const rise = this._bands.low - this._lowPrev;
      if (rise > NO_BPM_RISE && (this._time - this._lastNoBpmSpawn) > NO_BPM_MIN_GAP) {
        this._lastNoBpmSpawn = this._time;
        this._spawn('beat');
      }
      this._lowPrev = this._lowPrev + (this._bands.low - this._lowPrev) * Math.min(1, deltaTime * 6);
    }

    // --- notches heal ---
    for (let i = 0; i < MAX_NOTCHES; i++) {
      const b = this._notchBirth[i];
      const amt = b < 0 ? 0 : Math.max(0, 1 - (this._time - b) / NOTCH_CLOSE_SEC);
      if (i === 0) this._notchAmt.x = amt;
      else if (i === 1) this._notchAmt.y = amt;
      else if (i === 2) this._notchAmt.z = amt;
      else this._notchAmt.w = amt;
    }

    this._updateThickness();

    // --- uniforms ---
    // One 720p-reference pixel, in world units at the ring's depth.
    this._ringUniformsShared.uWorldPerPx.value = (2 * RING_DIST * Math.tan(fov / 2)) / REF_HEIGHT_PX;

    // The beat lands as LIGHT, not as a flash: a decaying lift of the whole
    // ring and a swell of the haze behind it, both from beatPhase directly so
    // there is no state to drift. Capped low enough that it never reads as a
    // strobe - the hierarchy still comes from the fragments.
    const bp2 = profile.beatPhase;
    if (bp2 != null) {
      const decay = (1 - clamp01(bp2)) * (1 - clamp01(bp2));
      this._ringUniformsShared.uPulse.value = 1 + PULSE_LIFT * decay;
      if (this._glowUniforms) {
        this._glowUniforms.uGain.value = GLOW_STRENGTH * (1 + GLOW_KICK * decay * this._bands.low);
      }
    } else {
      this._ringUniformsShared.uPulse.value = 1;
      if (this._glowUniforms) this._glowUniforms.uGain.value = GLOW_STRENGTH;
    }
    this._ringUniformsShared.uRampRot.value = this._rampRot();

    for (let r = 0; r < this._rings.length; r++) {
      const ring = this._rings[r];
      ring.uniforms.uRadius.value = this._radius * ring.radiusMul * this._contract;
    }
    // The second ring counter-rotates, which is the whole reason it is there:
    // two concentric circles turning the same way read as one flat disc.
    if (this._ringB) {
      this._ringB.mesh.rotation.z = this._time * SECOND_RING_SPIN;
      this._ringB.mesh.updateMatrix();
    }
    this._fragUniforms.uTime.value = this._time;
    this._fragUniforms.uRadius.value = this._radius * this._contract;

    this._placeGroup();

    if (this._ringDebugCtx) {
      this._ringDebugAccum += deltaTime;
      if (this._ringDebugAccum > 0.08) { this._ringDebugAccum = 0; this._drawRingDebug(); }
    }
  }

  getLightRig() {
    return this._rig;
  }

  dispose() {
    this._scene.fog = null;
    stageRig.release(RIG_OWNER);
    this._rig = null;
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._scene.remove(this._group);
    for (let i = 0; i < this._rings.length; i++) {
      this._rings[i].geo.dispose();
      this._rings[i].mat.dispose();
    }
    this._fragGeo.dispose();
    this._fragMat.dispose();
    if (this._glowMesh) {
      this._scene.remove(this._glowMesh);
      this._glowGeo.dispose();
      this._glowMat.dispose();
    }
    if (this._ringDebugCanvas && this._ringDebugCanvas.parentNode) {
      this._ringDebugCanvas.parentNode.removeChild(this._ringDebugCanvas);
    }
    if (typeof window !== 'undefined' && window.portalFragment) delete window.portalFragment;
  }
}
